import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBookmark,
  IconCheck,
  IconPrinter,
} from "@tabler/icons-react";
import { ComparePageSkeleton } from "./products_compare_page_skeleton";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Dialog } from "../../../../components/common/dialog";
import { Input } from "../../../../components/common/input";
import {
  Breadcrumbs,
  type BreadcrumbItem,
} from "../../../../components/common/breadcrumbs";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { ComparisonTable } from "../components/comparison-table";
import { AiComparisonRecommendation } from "../components/ai-comparison-recommendation";
import { RuleBasedComparisonRecommendation } from "../components/rule-based-comparison-recommendation";
import { useProductComparison } from "../hooks/useProductComparison";
import { NoticeBanner } from "../../../ai-search/presentation/pages/ai-search-page";
import { parseProductIds } from "../utils/comparisonUtils";
import { COMPARISON_ROWS } from "../components/comparison-rows-config";
import {
  useSavedComparison,
  createComparison,
} from "../../../comparisons/hooks/useSavedComparison";
import {
  useComparisonLookup,
  primeSavedComparisonCache,
} from "../../../comparisons/hooks/useComparisonLookup";
import { useApprovalStatus } from "../../../../hooks/useApprovalStatus";
import { useComparisonSaveUiStore } from "../../../../hooks/stores/useComparisonSaveUiStore";
import type { VariantComparison } from "../../data/variant-comparison.types";

type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * Injects landscape page setup for this route only, for the duration of the
 * mount.
 *
 * `@page` is document-level — it cannot be scoped by selector — so putting
 * this in index.css forced landscape on every Ctrl+P anywhere in the app
 * (orders, invoices, ...). Landscape is only wanted here, where the table is
 * a label column plus up to 3 product columns and would otherwise clip.
 * print-color-adjust forces background fills to print regardless of the
 * browser's "Background graphics" toggle, which is off by default in some
 * setups.
 */
function usePrintLandscape() {
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-print-landscape", "");
    style.textContent = `
      @media print {
        @page { size: landscape; margin: 12mm; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);
}

export function ProductsComparePage() {
  usePrintLandscape();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fromAi = searchParams.get("from") === "ai";
  const { status: approvalStatus } = useApprovalStatus();
  const showNoticeBanner = fromAi && approvalStatus !== "approved";
  const navTrail =
    (location.state as { breadcrumbTrail?: BreadcrumbItem[] } | null)
      ?.breadcrumbTrail ?? null;
  const idsParam = searchParams.get("ids");
  const savedId = searchParams.get("savedId");

  const { data: savedComparison, isLoading: isLoadingSaved } =
    useSavedComparison(savedId);

  // Saved comparisons supply their own variantIds; ad-hoc comparisons use `?ids=…`.
  const productIds = useMemo(() => {
    if (savedComparison) return savedComparison.variantIds;
    return parseProductIds(idsParam);
  }, [savedComparison, idsParam]);

  const { isLoading: isLoadingVariants, products } =
    useProductComparison(productIds);
  const isLoading = isLoadingSaved || isLoadingVariants;

  // Capture the latest AI result so we can persist it when the user saves.
  const latestAiResult = useRef<VariantComparison | null>(null);
  const handleAiResult = useCallback((comparison: VariantComparison) => {
    latestAiResult.current = comparison;
  }, []);

  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const playFlyToSaved = useComparisonSaveUiStore((s) => s.playFlyToSaved);

  useEffect(() => {
    document.body.style.overflow = "";
    document
      .getElementById("main-container")
      ?.classList.remove("thinking-scroll-lock");
  }, []);

  const displayProducts = products;

  // The IDs a save would persist — also what the lookup matches on, so a hit
  // is the very same row this page would otherwise create.
  const saveVariantIds = useMemo(
    () => displayProducts.map((p) => p.productTemplateId ?? p.id),
    [displayProducts],
  );

  // Opening `?savedId=…` already tells us it is saved; for an ad-hoc `?ids=…`
  // page (including after a refresh, which wipes saveState) ask the backend.
  const { data: previouslySaved, isLoading: isLoadingLookup } =
    useComparisonLookup(savedId ? null : saveVariantIds);

  const isViewingSaved = Boolean(savedComparison);
  const isAlreadySaved =
    isViewingSaved || Boolean(previouslySaved) || saveState === "saved";

  // Reopening a selection that is already saved (a reload, a pasted `?ids=…`
  // link) switches to the saved URL, so the page has one address per saved
  // comparison instead of two views of the same thing.
  useEffect(() => {
    if (savedId || !previouslySaved) return;
    const next = new URLSearchParams(searchParams);
    next.delete("ids");
    next.set("savedId", previouslySaved.id);
    navigate(
      { search: `?${next.toString()}` },
      { replace: true, state: location.state },
    );
  }, [savedId, previouslySaved, searchParams, navigate, location.state]);

  // The browser cannot choose the print destination — that is deliberate, and
  // Chrome preselects whatever the user last used. What it DOES take from the
  // page is document.title, which becomes the default filename in the OS
  // "Save As" sheet when the destination is Save as PDF. Without this it reads
  // "Procurtal" for every comparison.
  const handlePrint = useCallback(() => {
    const documentName =
      (isViewingSaved ? savedComparison?.name : null) ??
      displayProducts.map((p) => p.name).join(" vs ");

    const previousTitle = document.title;
    if (documentName) {
      // "/" and ":" are path separators on macOS/Windows save sheets.
      document.title = documentName.replace(/[/:\\]/g, "-").slice(0, 120);
    }

    const restore = () => {
      document.title = previousTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    // Fallback: afterprint is not fired by every browser/flow, and leaving the
    // tab titled after one comparison forever would be worse than a stale
    // filename.
    window.setTimeout(restore, 60_000);

    window.print();
  }, [isViewingSaved, savedComparison?.name, displayProducts]);

  const openSaveDialog = useCallback(() => {
    const defaultName = displayProducts.map((p) => p.name).join(" vs ");
    setSaveName(defaultName.slice(0, 120));
    setSaveDialogOpen(true);
  }, [displayProducts]);

  const handleSaveConfirm = useCallback(async () => {
    if (!saveName.trim()) return;
    setSaveDialogOpen(false);
    setSaveState("saving");
    try {
      const saved = await createComparison({
        name: saveName.trim(),
        variantIds: saveVariantIds,
        thumbnailUrl: displayProducts[0]?.image ?? undefined,
        aiComparison: latestAiResult.current ?? undefined,
      });
      setSaveState("saved");
      // Warm the caches before the URL change so the saved view renders from
      // what we just got back instead of flashing the skeleton.
      primeSavedComparisonCache(saved);
      // Same URL shape as opening it later from "My Comparisons", so a reload
      // or a shared link lands on the saved comparison rather than offering to
      // save it again.
      const next = new URLSearchParams(searchParams);
      next.delete("ids");
      next.set("savedId", saved.id);
      navigate(
        { search: `?${next.toString()}` },
        { replace: true, state: location.state },
      );
      try {
        playFlyToSaved({
          source: saveButtonRef.current,
          image: displayProducts[0]?.image ?? undefined,
        });
      } catch (flyErr) {
        // Cosmetic only — the save already succeeded, so this must never
        // downgrade saveState to "error".
        console.error("Fly-to-saved animation failed", flyErr);
      }
    } catch (err) {
      console.error("Failed to save comparison", err);
      setSaveState("error");
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to save comparison. Please try again.";
      toast.error(message);
    }
  }, [
    displayProducts,
    saveVariantIds,
    saveName,
    playFlyToSaved,
    searchParams,
    navigate,
    location.state,
  ]);

  if (isLoading) {
    return <ComparePageSkeleton productCount={productIds.length || 3} />;
  }

  if (!isLoading && displayProducts.length < 2) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-8 py-16 max-md:px-4 max-md:text-center">
        <Heading variant="h3" tone="dark">
          Couldn't load the comparison
        </Heading>
        <Button
          variant="secondary"
          leftIcon={<IconArrowLeft size={16} />}
          onClick={() =>
            location.key !== "default"
              ? navigate(-1)
              : navigate(AppRoutes.PRODUCTS)
          }
        >
          Go back and try again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6 max-md:gap-4 max-md:px-4">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          // Where the user actually came from wins over the saved view: saving
          // swaps the URL to `?savedId=…`, and that must not rewrite the trail
          // under them.
          ...(fromAi
            ? [{ label: "AI Search", path: `${AppRoutes.AI_SEARCH}?results=1` }]
            : (navTrail ??
              (isViewingSaved
                ? [{ label: "My Comparisons", path: AppRoutes.MY_COMPARISONS }]
                : [{ label: "Catalog", path: AppRoutes.PRODUCTS }]))),
          {
            label: isViewingSaved
              ? (savedComparison?.name ?? "Compare products")
              : "Compare products",
          },
        ]}
      />

      {/* Page header */}
      <div className="flex items-center justify-between gap-4 max-md:flex-wrap max-md:gap-3">
        <Heading variant="h2" tone="dark">
          {isViewingSaved
            ? (savedComparison?.name ?? "Compare products")
            : "Compare products"}
        </Heading>
        <div className="flex items-center gap-3 print:hidden">
          {/* Rendered even once saved: "Saved" is the confirmation the save
              landed, and a disabled button is what blocks a second save. */}
          <Button
            ref={saveButtonRef}
            variant="secondary"
            size="sm"
            leftIcon={
              isAlreadySaved ? (
                <IconCheck size={15} />
              ) : saveState === "error" ? (
                <IconAlertCircle size={15} className="text-accentError" />
              ) : (
                <IconBookmark size={15} />
              )
            }
            onClick={openSaveDialog}
            disabled={
              isAlreadySaved || isLoadingLookup || saveState === "saving"
            }
          >
            {isAlreadySaved
              ? "Saved"
              : saveState === "saving"
                ? "Saving…"
                : saveState === "error"
                  ? "Try again"
                  : "Save Comparison"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<IconPrinter size={15} />}
            onClick={handlePrint}
          >
            Print
          </Button>
        </div>
      </div>

      {showNoticeBanner && (
        <div className="print:hidden">
          <NoticeBanner />
        </div>
      )}

      <ComparisonTable products={displayProducts} rows={COMPARISON_ROWS} />

      <RuleBasedComparisonRecommendation products={displayProducts} />

      <AiComparisonRecommendation
        variants={displayProducts}
        initialComparison={savedComparison?.aiComparison ?? null}
        onResult={handleAiResult}
      />

      <Dialog
        open={saveDialogOpen}
        setOpen={setSaveDialogOpen}
        title="Save comparison"
        titleSize="lg"
        titleAlignment="left"
        showButtons={false}
        showCloseButton
        content={
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveConfirm();
            }}
            className="flex flex-col gap-4"
          >
            <Text variant="bodySm" tone="muted">
              Give this comparison a name so you can find it later.
            </Text>
            <Input
              label="Name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              maxLength={120}
              autoFocus
            />
            <div className="flex items-center justify-end gap-3 pt-1">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setSaveDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={!saveName.trim()}
                leftIcon={<IconBookmark size={16} />}
              >
                Save
              </Button>
            </div>
          </form>
        }
      />
    </div>
  );
}
